/**
 * Skin-lagret — permanent arkitektur för kosmetik (fas 5).
 *
 * Rendering slår ALDRIG upp utseende direkt från unit-typ. Kedjan är:
 *   unit → spelarens valda skinId (per family) → SkinDefinition → visual.
 * Default-skinnet är färgade geometriska former (en form per family).
 * I fas 5 registreras köpta skins med texturer i samma register, och
 * entitlements styr vilket skinId en spelare får välja.
 */
import type { Family } from '../sim/config';
import type { PlayerId } from '../sim/state';

export type ShapeKind = 'circle' | 'triangle' | 'square' | 'diamond' | 'pentagon' | 'wing';

/** Beskriver hur en unit av given family+tier ska se ut under ett skin. */
export interface UnitVisual {
  shape: ShapeKind;
  /** Radie i world units; tier skalar storleken. */
  radius: number;
  /** null = använd spelarfärgen. Skins kan sätta egen palett. */
  fillColor: number | null;
  outlineColor: number | null;
}

export interface SkinDefinition {
  id: string;
  name: string;
  unitVisual(family: Family, tier: number): UnitVisual;
  mothershipVisual(): UnitVisual;
  baseVisual(): UnitVisual;
}

/** Beslut 2026-07-15: Blå (p1) vs Röd (p2). */
export const PLAYER_COLORS: Record<PlayerId, { fill: number; outline: number }> = {
  p1: { fill: 0x3b82f6, outline: 0x93c5fd },
  p2: { fill: 0xef4444, outline: 0xfca5a5 },
};

const FAMILY_SHAPES: Record<Family, ShapeKind> = {
  swarm: 'circle',
  ranged: 'triangle',
  armor: 'square',
  antiarmor: 'diamond',
  siege: 'pentagon',
  flyer: 'wing',
};

const TIER_RADIUS = [0.35, 0.5, 0.7] as const;

const defaultSkin: SkinDefinition = {
  id: 'default',
  name: 'Standard',
  unitVisual(family, tier) {
    return {
      shape: FAMILY_SHAPES[family],
      radius: TIER_RADIUS[tier - 1] ?? 0.35,
      fillColor: null,
      outlineColor: null,
    };
  },
  mothershipVisual() {
    return { shape: 'pentagon', radius: 1.4, fillColor: null, outlineColor: 0xffffff };
  },
  baseVisual() {
    // Mörk fyllnad + spelarfärgad kant: units förblir läsbara ovanpå basen.
    return { shape: 'square', radius: 2.2, fillColor: 0x0f1a30, outlineColor: null };
  },
};

const registry = new Map<string, SkinDefinition>([[defaultSkin.id, defaultSkin]]);

export function registerSkin(skin: SkinDefinition): void {
  registry.set(skin.id, skin);
}

export function getSkin(skinId: string): SkinDefinition {
  return registry.get(skinId) ?? defaultSkin;
}

/**
 * Spelarens skinval. I mål 1 har alla 'default'; i fas 5 fylls detta från
 * entitlements (server-side) och lobby-valet.
 */
export interface PlayerSkinSelection {
  unitSkinByFamily: Record<Family, string>;
  mothershipSkin: string;
  baseSkin: string;
}

export function defaultSkinSelection(): PlayerSkinSelection {
  return {
    unitSkinByFamily: {
      swarm: 'default',
      ranged: 'default',
      armor: 'default',
      antiarmor: 'default',
      siege: 'default',
      flyer: 'default',
    },
    mothershipSkin: 'default',
    baseSkin: 'default',
  };
}
